import { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useDispatch } from 'shared/lib/hooks/useDispatch';
import { addToCartSize } from 'src/store/goods';
import { GoodsErrorsList, IProductError } from 'entities/products/lib/checkProductToBuy';
import { InputCheckbox } from '@nebo-team-dev/vobaza.ui.inputs.input-checkbox';
import { Button } from '@nebo-team-dev/vobaza.ui.button/dist';
import { metric } from 'features/metric';
import CartListItem from './ListItem';
import CartItemChangeModal from '../Modal/CartItemChangeModal';
import type { IGoodInCart } from 'entities/products';
import { api } from '../../../global/api';
import styles from './styles.module.scss';
import {pluralizeNoun} from "../../../shared/lib/pluralizeNoun";

type Props = {
  goods: IGoodInCart[];
  withCountChange?: string | boolean;
  setOrderPrice: (price: number) => void;
  onChangeGoods?: (goods: IGoodInCart[]) => void;
  setIsCheckoutDisabled?: (boolean) => void;
  onErrorChangeGoods?: () => void;
  weight?: number;
};

const CartList: FC<Props> = ({
  goods,
  withCountChange = '',
  setOrderPrice,
  onChangeGoods,
  setIsCheckoutDisabled,
  onErrorChangeGoods,
  weight,
}) => {
  const [productError, setProductError] = useState<IProductError>(
    withCountChange ? { ...GoodsErrorsList.COUNT_IS_CHANGED, description: withCountChange.toString() } : null
  );
  const [isLoading, setIsLoading] = useState(false);

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const router = useRouter();

  const dispatch = useDispatch();
  const goodsAmount = goods.reduce((acc, good) => acc + good.quantity, 0);
  const goShopping = () => {
    router.push('/');
  };
  const closeModal = () => {
    setProductError(null);
  };
  const changeActiveCheckBoxes = (id: number) => {
    onChangeGoods(
      goods.map((good) => {
        if (good.product.id === id) {
          return { ...good, isChecked: !good.isChecked };
        }
        return good;
      })
    );
  };
  const chooseAllHandler = () => {
    if (isAllSelected) {
      setIsAllSelected(false);
      onChangeGoods(goods.map((good) => ({ ...good, isChecked: false })));
      return;
    }
    onChangeGoods(goods.map((good) => ({ ...good, isChecked: good.isRemoved ? false : true })));
    setIsAllSelected(true);
  };

  const removeMany = async () => {
    const filteredGoods = goods.filter((good) => good.isChecked);
    const removedItems = [];
    try {
      setIsLoading(true);
      setIsCheckoutDisabled(true);
      new Promise<number>((res, rej) => {
        (async () => {
          for (let i = 0; i < filteredGoods.length; i++) {
            try {
              await api.removeById(filteredGoods[i].product.id);

              metric.removeProduct({ product: filteredGoods[i].product, quantity: filteredGoods[i].quantity });
              removedItems.push(filteredGoods[i].product.id);
              if (i + 1 === filteredGoods.length) {
                res(filteredGoods[i].product.id);
              }
            } catch (error) {
              rej(error);
            }
          }
        })();
      }).then(() => {
        onChangeGoods(
          goods.map((good) =>
            removedItems.includes(good.product.id) ? { ...good, isRemoved: true, isChecked: false } : good
          )
        );
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsCheckoutDisabled(false);
      setIsLoading(false);
    }
  };

  const removeItem = async (id: number, quantity: number) => {
    const good = goods.find((good) => good.product.id === id);
    setIsLoading(true);
    setIsCheckoutDisabled(true);
    try {
      const res = await api.removeGoodFromCart(id, {
        quantity,
        include: 'prices',
      });
      metric.removeProduct({ product: good.product, quantity: good.quantity });
      onChangeGoods(
        goods.map((good) => (good.product.id === id ? { ...good, isRemoved: true, isChecked: false } : good))
      );
      setOrderPrice(res.data.data.order_price / 100);
    } catch (error) {
      console.error(error);
    } finally {
      setIsCheckoutDisabled(false);
      setIsLoading(false);
    }
  };

  const restoreItem = (id: number) => {
    onChangeGoods(
      goods.map((good) => (good.product.id === id ? { ...good, isRemoved: false, isChecked: true } : good))
    );
  };
  const changeItem = async (id: number, quantity: number) => {
    if (quantity === 0) return;
    setIsCheckoutDisabled(true);
    const productInCart = goods.find((good) => good.product.id === id);
    try {
      let res = null;
      if (quantity > 0) {
        res = await api.addGoodToCart(id, { quantity, include: 'prices' });

        metric.addProduct({ product: productInCart.product, quantity });
      } else {
        res = await api.removeGoodFromCart(id, {
          quantity: quantity * -1,
          include: 'prices',
        });
        metric.removeProduct({ product: productInCart.product, quantity: quantity * -1 });
      }
      dispatch(addToCartSize(res.data.data.changed_quantity));
      onChangeGoods(
        goods.map((item) => {
          if (item.product.id === id) {
            return {
              ...item,
              price: res.data.data.price / 100,
              list_price: res.data.data.list_price / 100 || null,
              quantity: item.quantity + res.data.data.changed_quantity,
            };
          }
          return {
            ...item,
          };
        })
      );
      setOrderPrice(res.data.data.order_price / 100);
      if (res.data.data.changed_quantity !== quantity) {
        setProductError(GoodsErrorsList.COUNT_IS_CHANGED);
      }
      setIsCheckoutDisabled(false);
      return res.data.data.changed_quantity;
    } catch (error) {
      const status = error.response?.status;
      const errors = error.response?.data?.errors;

      setIsCheckoutDisabled(false);

      if (status === 422) {
        if (errors?.length) {
          const errorTitle = errors[0].title;

          if (errorTitle === 'Товар более не доступен. Пожалуйста, выберите другой') {
            setProductError(GoodsErrorsList.NOT_FOUND);
          } else {
            setProductError(GoodsErrorsList.OVERFULL_CART);
          }
        }

        if (onErrorChangeGoods) onErrorChangeGoods();
        throw new Error();
      }

      if (status === 404) {
        setProductError(GoodsErrorsList.NOT_FOUND);
        onChangeGoods(goods.filter((good) => good.product.id !== id));
        throw new Error();
      }

      if (status >= 500 && status < 600) {
        setProductError(GoodsErrorsList.SERVER);
        throw new Error();
      }
    }
  };
  useEffect(() => {
    const notRemovedGoods = goods.filter((good) => !good.isRemoved);
    const checkedGoods = goods.filter((good) => good.isChecked);
    if (notRemovedGoods.length) {
      setIsAllSelected(checkedGoods.length === notRemovedGoods.length);
    }
  }, [goods]);

  return (
    <div className={styles.cartList}>
      {!!productError && (
        <CartItemChangeModal onClose={closeModal} description={productError.description} title={productError.title} />
      )}
      {goods && goods.length > 0 ? (
        <div className={`${styles.cartContent} ${styles.small}`}>
          <div className={styles.cartHeader}>
            <h2 className={styles.cartTitle}>Корзина</h2>
            <span>
              {goodsAmount} {pluralizeNoun(goodsAmount, ['товар', 'товара', 'товаров'])} • {weight || 0} кг
            </span>
          </div>
          <div className={styles.checkAll}>
            <InputCheckbox
              className={styles.checkAll__checkBox}
              onClick={chooseAllHandler}
              initialValue={isAllSelected}
              label="Выбрать все"
            />
            <Button
              disabled={!goods.filter((good) => good.isChecked).length}
              onClick={removeMany}
              variation="ternary"
              text="Удалить выбранное"
            />
          </div>
          {goods.map((good) => (
            <div className={styles.cartListItemBlock} key={good.product.id}>
              <InputCheckbox
                disabled={isLoading || good.isRemoved}
                initialValue={good.isChecked}
                onClick={() => {
                  changeActiveCheckBoxes(good.product.id);
                }}
                className={styles.checkbox}
                label={''}
              />
              <CartListItem
                isCartLoading={isLoading}
                good={good}
                deleteItem={removeItem}
                changeItem={changeItem}
                setIsCheckoutDisabled={setIsCheckoutDisabled}
                restoreItem={restoreItem}
              />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className={styles.cartEmptyList}>пусто</div>
          <div className={styles.cartButtons}>
            <Button
              style={{
                color: '#af1ebe',
                backgroundColor: '#f2f2f2',
                fontWeight: 500,
              }}
              text="Продолжить покупки"
              onClick={goShopping}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default CartList;
